'use client'

import { useRef } from 'react'
import Image from 'next/image'
import { motion, type Variants } from 'motion/react'
interface ValuePropositionViewProps {
  badge: string
  title: string
  bigStat: string
  instituteLine: string
  aboutLeft: string
  aboutRight: string
  /** Photo inside the shaped frame (home.md: valueImage) */
  image?: string
}

// Real NCP classroom photo (replaces the Mt. Fuji stock image)
const DEFAULT_IMAGE = '/images/classroom/class-wide.webp'

export function ValuePropositionView({
  badge,
  title,
  bigStat,
  instituteLine,
  aboutLeft,
  aboutRight,
  image,
}: ValuePropositionViewProps) {
  const heroImage = image || DEFAULT_IMAGE
  const sectionRef = useRef<HTMLElement>(null)

  const ease = [0.16, 1, 0.3, 1] as [number, number, number, number]

  const revealVariants: Variants = {
    hidden: { y: 12 },
    visible: (i: number) => ({
      y: 0,
      transition: { delay: i * 0.08, duration: 0.5, ease },
    }),
  }

  const scaleVariants: Variants = {
    hidden: { opacity: 0.96 },
    visible: (i: number) => ({
      opacity: 1,
      transition: { delay: i * 0.08, duration: 0.55, ease },
    }),
  }

  return (
    <section
      id="why"
      ref={sectionRef}
      className="relative overflow-hidden bg-white py-16 md:py-24"
    >
      <div className="section-container max-w-6xl">
        <div className="relative">
          {/* Top bar: eyebrow */}
          <motion.div
            className="absolute -top-3 z-10 sm:-top-2 md:top-0 lg:top-4"
            variants={revealVariants}
            custom={0}
            initial={false}
            animate="visible"
          >
            <span className="section-eyebrow">{badge}</span>
          </motion.div>

          {/* Big clipped hero image */}
          <motion.figure
            className="relative"
            variants={scaleVariants}
            custom={4}
            initial={false}
            animate="visible"
          >
            <svg
              className="w-full"
              width="100%"
              height="100%"
              viewBox="0 0 100 40"
              aria-hidden
            >
              <defs>
                <clipPath
                  id="why-clip-inverted"
                  clipPathUnits="objectBoundingBox"
                >
                  <path
                    d="M0.0998072 1H0.422076H0.749756C0.767072 1 0.774207 0.961783 0.77561 0.942675V0.807325C0.777053 0.743631 0.791844 0.731953 0.799059 0.734076H0.969813C0.996268 0.730255 1.00088 0.693206 0.999875 0.675159V0.0700637C0.999875 0.0254777 0.985045 0.00477707 0.977629 0H0.902473C0.854975 0 0.890448 0.138535 0.850165 0.138535H0.0204424C0.00408849 0.142357 0 0.180467 0 0.199045V0.410828C0 0.449045 0.0136283 0.46603 0.0204424 0.469745H0.0523086C0.0696245 0.471019 0.0735527 0.497877 0.0733523 0.511146V0.915605C0.0723903 0.983121 0.090588 1 0.0998072 1Z"
                    fill="#D9D9D9"
                  />
                </clipPath>
              </defs>
              <image
                clipPath="url(#why-clip-inverted)"
                preserveAspectRatio="xMidYMid slice"
                width="100%"
                height="100%"
                xlinkHref={heroImage}
              />
            </svg>
          </motion.figure>

          {/* Stats / markers row */}
          <div className="flex flex-wrap items-center justify-between py-3 text-sm lg:justify-start">
            <motion.p
              className="type-body-lg max-w-xl"
              variants={revealVariants}
              custom={5}
              initial={false}
              animate="visible"
            >
              {instituteLine}
            </motion.p>

            <motion.div
              className="lg:absolute lg:bottom-20 lg:-right-2 xl:bottom-24"
              variants={revealVariants}
              custom={6}
              initial={false}
              animate="visible"
            >
              <span className="type-display-stat text-2xl sm:text-3xl lg:text-4xl">
                {bigStat}
              </span>
            </motion.div>
          </div>
        </div>

        {/* Main content grid */}
        <div className="grid gap-8 md:grid-cols-3">
          <div className="md:col-span-2">
            <h2 className="type-editorial-title mb-8">{title}</h2>

            <motion.div
              className="grid gap-6 md:grid-cols-2 md:gap-8"
              variants={revealVariants}
              custom={9}
              initial={false}
              animate="visible"
            >
              <p className="type-body">{aboutLeft}</p>
              <p className="type-body">{aboutRight}</p>
            </motion.div>
          </div>

          <div className="md:col-span-1 flex items-start justify-end">
            <motion.div
              className="flex justify-end"
              variants={revealVariants}
              custom={12}
              initial={false}
              animate="visible"
            >
              <Image
                src="/ncp-logo.webp"
                alt="Nippon Center Pakistan"
                width={280}
                height={112}
                className="h-24 w-auto sm:h-28 md:h-32 lg:h-36"
                priority={false}
              />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  )
}
